import type { ApiSuccess, CreateNoteInput, Note, PaginatedResponse } from '@just-us/shared'
/**
 * hooks/use-notes.ts
 *
 * TanStack Query hooks for notes:
 * - useNotes(filter)    — paginated list (inbox | sent | archived)
 * - useNote(id)         — single note by id
 * - useCreateNote()     — POST /notes
 * - useOpenNote()       — PATCH /notes/:id/open
 * - useDeleteNote()     — DELETE /notes/:id
 * - useUploadDoodle()   — POST /notes/doodle (multipart)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api-client'
import { getToken } from '../store/auth'

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

export const NOTES_QUERY_KEY = (filter: string) => ['notes', filter] as const
export const NOTE_QUERY_KEY = (id: string) => ['note', id] as const

// ---------------------------------------------------------------------------
// useNotes
// ---------------------------------------------------------------------------

export const useNotes = (filter: 'inbox' | 'sent' | 'archived' = 'inbox') =>
  useQuery({
    queryKey: NOTES_QUERY_KEY(filter),
    queryFn: () => apiFetch<PaginatedResponse<Note>>(`/notes?filter=${filter}&page=1&limit=20`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })

// ---------------------------------------------------------------------------
// useNote — single note fetched from the inbox/sent list cache
// ---------------------------------------------------------------------------

export const useNote = (id: string) =>
  useQuery({
    queryKey: NOTE_QUERY_KEY(id),
    queryFn: () => apiFetch<ApiSuccess<Note>>(`/notes/${id}`),
    staleTime: 60_000,
    enabled: !!id,
  })

// ---------------------------------------------------------------------------
// useCreateNote
// ---------------------------------------------------------------------------

export const useCreateNote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateNoteInput) =>
      apiFetch<ApiSuccess<Note>>('/notes', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// ---------------------------------------------------------------------------
// useOpenNote
// ---------------------------------------------------------------------------

export const useOpenNote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ApiSuccess<Note>>(`/notes/${id}/open`, { method: 'PATCH' }),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      queryClient.invalidateQueries({ queryKey: NOTE_QUERY_KEY(id) })
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteNote
// ---------------------------------------------------------------------------

export const useDeleteNote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ApiSuccess<{ deleted: boolean }>>(`/notes/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// ---------------------------------------------------------------------------
// useUploadDoodle — multipart/form-data upload
// Does NOT use apiFetch because we must NOT set Content-Type header manually
// (browser sets it with the correct boundary for multipart).
// ---------------------------------------------------------------------------

export const useUploadDoodle = () =>
  useMutation({
    mutationFn: async (blob: Blob): Promise<{ encrypted_key: string; size: number }> => {
      const token = getToken()
      const formData = new FormData()
      formData.append('file', blob, 'doodle.png')

      const res = await fetch(`${API_BASE}/notes/doodle`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
      }

      const json = (await res.json()) as { data: { encrypted_key: string; size: number } }
      return json.data
    },
  })
